import React, { ChangeEvent, PureComponent } from 'react';

import styled, { css } from 'styled-components';

import Box from '../atoms/Box/Box';
import ErrorFormField from '../atoms/ErrorFormField/ErrorFormField';
import IconWarning from '../atoms/IconWarning/IconWarning';

import { breakpoint } from 'theme';
import { styleFormField, styleLabelInput } from '@shared/markup/styles';

const Label = styled.label`
  ${styleLabelInput}
`;

const Input = styled.input<{
  error: string | boolean;
  extraText?: (text?: string) => JSX.Element | null;
}>`
  display: block;
  width: 100%;
  border-radius: 2px;
  box-shadow: 0 1px 4px 0 ${({ theme }) => theme.transparentColors.lightIron50};
  border: solid 1px ${({ theme }) => theme.colors.lightIron};
  background-color: ${({ theme }) => theme.colors.white};
  padding: 1em 0.875em;
  color: ${({ theme }) => theme.colors.cornflowerBlue};
  font-size: 16px;
  line-height: 1.1;

  ${props =>
    props.extraText &&
    `
    padding-right: 80px;
  `}

  & + ${ErrorFormField} {
    opacity: 0;
    visibility: hidden;
  }

  &:focus {
    box-shadow: 0 1px 4px 0
      ${({ theme }) => theme.transparentColors.puertoRico50};
    outline: 2px solid ${({ theme }) => theme.colors.turquoise};
    outline-offset: 0;
    ${({ error }) => error && styleFormField};
    ${({ error }) =>
      error &&
      css`
        & + ${ErrorFormField} {
          opacity: 1;
          visibility: visible;
          transition-delay: 0s;
        }
      `};
  }

  ${({ error }) => error && styleFormField}

  ${breakpoint.m`
    font-size: 20px;
    padding-top: 0.5em;
    padding-bottom: 0.5em;

    ${({ extraText }) =>
      extraText &&
      `
      padding-right: 100px;
    `}
  `}
`;

const BoxExtraElement = styled(Box)`
  position: absolute;
  top: 1.5em;
  right: 0.625em;
  bottom: 0;
  display: flex;
  align-items: center;
`;

type CheckoutInputProps = {
  className?: string;
  label?: string;
  name?: string;
  onChange: (e?: ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  value?: string;
  extraText?: (text?: string) => JSX.Element | null;
  autoComplete?: string;
  error?: string;
  touched?: string;
  tabIndex?: number;
  onFocus?: () => void;
};

class CheckoutInput extends PureComponent<CheckoutInputProps> {
  static defaultProps = {
    type: 'text',
    value: '',
  };

  render() {
    const {
      label,
      name,
      onChange,
      type,
      value,
      extraText,
      className,
      autoComplete,
      touched,
      error,
      tabIndex,
      onFocus,
    } = this.props;

    return (
      <Box className={className}>
        <Label>
          <Box mb={0.5}>{label}</Box>

          <Input
            type={type}
            name={name}
            value={value}
            id={name}
            onChange={onChange}
            autoComplete={autoComplete}
            error={touched ? !!error : undefined}
            tabIndex={tabIndex}
            onFocus={onFocus}
            extraText={extraText}
          />
          <ErrorFormField error={error} />

          {!error && extraText && (
            <BoxExtraElement onClick={onFocus}>{extraText()}</BoxExtraElement>
          )}

          {touched && error && <IconWarning />}
        </Label>
      </Box>
    );
  }
}

export default CheckoutInput;
